import { Download } from "lucide-react";
import { useRef } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { downloadElementAsPng } from "../lib/chartExport";
import type { JsonObject, QuerygenChartConfig } from "../types";

type QueryResultChartProps = {
  data: JsonObject[];
  chartConfig: QuerygenChartConfig;
  downloadFilename?: string;
};

const colors = ["#2563eb", "#14b8a6", "#f59e0b", "#ef4444", "#7c3aed"];

const formatNumber = (value: unknown): string => {
  if (typeof value === "number") {
    return value.toLocaleString();
  }

  return String(value ?? "");
};

export function QueryResultChart({
  data,
  chartConfig,
  downloadFilename = "query-chart.png",
}: QueryResultChartProps) {
  const chartExportRef = useRef<HTMLDivElement | null>(null);

  if (!chartConfig || !data.length) {
    return null;
  }

  const { chartType, xAxis, yAxis, title, description } = chartConfig;

  const handleDownload = async () => {
    const chartElement = chartExportRef.current;
    if (!chartElement) {
      return;
    }

    try {
      await downloadElementAsPng(chartElement, downloadFilename);
    } catch (error) {
      console.error("Failed to export chart PNG", error);
    }
  };

  return (
    <div className="mt-5 overflow-hidden rounded-md border border-[rgba(17,17,17,0.08)] bg-[#fcfcfc]">
      <div className="flex items-start justify-between gap-4 border-b border-[rgba(17,17,17,0.06)] px-5 py-4">
        <div>
          <div className="text-[14px] font-medium text-[#18181b]">{title}</div>
          <div className="mt-1 text-[12px] text-[#6b7280]">{description}</div>
        </div>
        <button
          type="button"
          onClick={() => void handleDownload()}
          className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-full border border-[rgba(17,17,17,0.08)] bg-white px-3 py-1.5 text-[12px] font-medium text-[#5f6470] transition-colors hover:text-[#111111]"
        >
          <Download className="h-3.5 w-3.5" />
          PNG
        </button>
      </div>
      <div ref={chartExportRef} className="bg-[#fcfcfc] px-4 py-4">
        <div className="h-[320px]">
          <ResponsiveContainer width="100%" height="100%">
            {chartType === "line" ? (
              <LineChart data={data}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey={xAxis} tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip formatter={(value) => formatNumber(value)} />
                <Legend />
                {yAxis.map((key, index) => (
                  <Line
                    key={key}
                    type="monotone"
                    dataKey={key}
                    stroke={colors[index % colors.length]}
                    strokeWidth={2.5}
                    dot={false}
                  />
                ))}
              </LineChart>
            ) : chartType === "pie" || chartType === "donut" ? (
              <PieChart>
                <Tooltip formatter={(value) => formatNumber(value)} />
                <Legend />
                <Pie
                  data={data}
                  dataKey={yAxis[0]}
                  nameKey={xAxis}
                  outerRadius={108}
                  innerRadius={chartType === "donut" ? 64 : 0}
                  paddingAngle={2}
                >
                  {data.map((_, index) => (
                    <Cell key={`cell-${index}`} fill={colors[index % colors.length]} />
                  ))}
                </Pie>
              </PieChart>
            ) : (
              <BarChart data={data}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey={xAxis} tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip formatter={(value) => formatNumber(value)} />
                <Legend />
                {yAxis.map((key, index) => (
                  <Bar
                    key={key}
                    dataKey={key}
                    fill={colors[index % colors.length]}
                    radius={[6, 6, 0, 0]}
                  />
                ))}
              </BarChart>
            )}
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}
